/**
 * Timing helpers for the learning-journey runtime.
 *
 * Pure TS, zero dependency on the Studio / Transport. Callers are
 * expected to pass the relevant tempo data in (BPM, PPQ, playback
 * rate), this keeps the helpers reusable from tests and from any
 * future runtime that does not own a `Transport`.
 *
 * Conventions
 * -----------
 * - `bpm` is the *target* tempo in beats per minute, as authored in
 *   the MIDI file. The S4 60% slow-down is applied through
 *   `playbackRate`, not by mutating `bpm` upstream.
 * - `playbackRate = 1` means "play at the authored tempo". Values
 *   below 1 slow down (e.g. 0.6 = 60% of nominal speed → bars and
 *   beats become longer in real time).
 * - `ppq` is pulses-per-quarter-note (= ticks per quarter note),
 *   the standard MIDI resolution carried on every Track.
 *
 * Edge cases
 * ----------
 * Invalid inputs (`bpm <= 0`, `playbackRate <= 0`, `ppq <= 0`) yield
 * `0` rather than `Infinity` / `NaN`, so the caller never has to
 * defensive-check a bare number returned by these helpers.
 */

const MS_PER_MINUTE = 60_000;

/**
 * Real-time duration of one beat (one quarter note) in milliseconds,
 * accounting for the playback rate.
 *
 * Example: `msPerBeat(120, 1) === 500`, `msPerBeat(120, 0.6) ≈ 833.33`.
 */
export function msPerBeat(bpm: number, playbackRate: number = 1): number {
  if (bpm <= 0 || playbackRate <= 0) {
    return 0;
  }

  return MS_PER_MINUTE / (bpm * playbackRate);
}

/**
 * Convert a beat count to milliseconds at the given tempo and rate.
 *
 * Example: 8 beats at 120 BPM played at 60% speed
 * → `beatsToMs(8, 120, 0.6) ≈ 6666.66` (the burst window in S4).
 */
export function beatsToMs(
  beats: number,
  bpm: number,
  playbackRate: number = 1,
): number {
  return beats * msPerBeat(bpm, playbackRate);
}

/**
 * Convert a duration in milliseconds back to a beat count.
 *
 * Inverse of `beatsToMs`. Returns `0` for invalid tempo inputs.
 */
export function msToBeats(
  ms: number,
  bpm: number,
  playbackRate: number = 1,
): number {
  const beatMs = msPerBeat(bpm, playbackRate);

  if (beatMs === 0) {
    return 0;
  }

  return ms / beatMs;
}

/**
 * Convert a tick count to milliseconds.
 *
 * `ticks / ppq` gives a beat count, which is then mapped to ms via
 * the current playback rate.
 */
export function ticksToMs(
  ticks: number,
  ppq: number,
  bpm: number,
  playbackRate: number = 1,
): number {
  if (ppq <= 0) {
    return 0;
  }

  return beatsToMs(ticks / ppq, bpm, playbackRate);
}

/**
 * Convert a duration in milliseconds back to a tick count.
 *
 * Round-trip with `ticksToMs` is exact for whole-tick inputs at
 * sane tempi (no float drift in practice, see test).
 */
export function msToTicks(
  ms: number,
  ppq: number,
  bpm: number,
  playbackRate: number = 1,
): number {
  if (ppq <= 0) {
    return 0;
  }

  return msToBeats(ms, bpm, playbackRate) * ppq;
}

/**
 * Compute the tick that marks the start of the bar containing `tick`,
 * relative to a phrase's own bar grid.
 *
 * The phrase carries its `startTick` (position of the first bar of the
 * phrase in the song) but not the bar lengths in ticks. The caller
 * supplies `ticksPerBar`, typically `ppq * beatsPerBar` from the
 * song's time signature.
 *
 * The returned tick is always `>= phrase.startTick`. Ticks before the
 * phrase's start are clamped to `phrase.startTick` rather than
 * extrapolated backwards, the burst detector does not care about
 * out-of-phrase positions, and clamping keeps the API total.
 *
 * Returns `tick` itself (no-op) when `ticksPerBar <= 0`, so a misbuilt
 * caller never propagates an Infinity / NaN downstream.
 */
export function barStartTickFromPhrase(
  phraseStartTick: number,
  tick: number,
  ticksPerBar: number,
): number {
  if (ticksPerBar <= 0) {
    return tick;
  }

  if (tick <= phraseStartTick) {
    return phraseStartTick;
  }

  const offset = tick - phraseStartTick;
  const barsFromStart = Math.floor(offset / ticksPerBar);

  return phraseStartTick + barsFromStart * ticksPerBar;
}
